import { QueryClient } from "react-query";

/**
 * The application's single React Query client.
 *
 * Centralizing the default options here directly targets two problems found in
 * the audit:
 *
 *  1. Excessive refetching — most Firestore-backed hooks specified no
 *     `staleTime` (so React Query treated their data as immediately stale and
 *     refetched on every mount) and inherited React Query v3's default
 *     `refetchOnWindowFocus: true`, causing a fresh Firestore read every time
 *     the tab regained focus. A modest default `staleTime` plus disabling
 *     refetch-on-focus eliminates the redundant reads while remaining safe:
 *     all mutations bust the workspace `cacheKey` (which changes the query key
 *     and forces a fresh fetch), and real-time hooks (`subscribe: true`) keep
 *     updating live regardless of `staleTime`.
 *
 *  2. Inconsistent behavior — every hook chose its own retry/refetch policy.
 *     These defaults give the whole app one predictable baseline; hooks that
 *     genuinely need different behavior (e.g. the API order hooks with a
 *     5-minute stale window) still override locally.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
      cacheTime: 5 * 60_000,
    },
  },
});
